import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, MapPin, Wallet, Timer, Users, Building2, CheckCircle, Send, ExternalLink } from 'lucide-react';

function RationaleModal({ isOpen, onClose, onSubmit, roleTitle, Company, role }) {
  const [rationale, setRationale] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("company");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const videoRef = useRef(null);
  const textareaRef = useRef(null);
  const overlayContentRef = useRef(null);

  if (!isOpen || !role) return null;

  const handleSubmit = async () => {
    if (!rationale.trim()) {
      return;
    }
    setIsSubmitting(true);
    await onSubmit(rationale.trim());
    setIsSubmitting(false);
    setRationale('');
  };

  const handleClose = () => {
    setRationale('');
    setIsCollapsed(false);
    onClose();
  };

  const getCoverImageUrl = () => {
    const fallbackImageUrl = "https://placehold.co/400x300/e2e8f0/64748b?text=No+Image";
    const imageField = role["Role Cover Image (from Startup)"];

    if (!imageField) return fallbackImageUrl;
    
    if (typeof imageField === 'string') return imageField;
    
    if (Array.isArray(imageField) && imageField.length > 0) {
      const firstImage = imageField[0];
      if (firstImage && typeof firstImage === 'object') {
        if (firstImage.url) return firstImage.url;
        if (firstImage.thumbnails) {
          const sizes = ['large', 'full', 'small'];
          for (const size of sizes) {
            if (firstImage.thumbnails[size] && firstImage.thumbnails[size].url) {
              return firstImage.thumbnails[size].url;
            }
          }
        }
      }
      if (typeof firstImage === 'string') return firstImage;
    }
    
    if (typeof imageField === 'object' && imageField !== null) {
      if (imageField.url) return imageField.url;
      if (imageField.thumbnails) {
        const sizes = ['large', 'full', 'small'];
        for (const size of sizes) {
          if (imageField.thumbnails[size] && imageField.thumbnails[size].url) {
            return imageField.thumbnails[size].url;
          }
        }
      }
    }
    
    return fallbackImageUrl;
  };

  const hasSpotlights = role.spotlightDetails && role.spotlightDetails.length > 0;

  const companyOnlySpotlights = hasSpotlights ? role.spotlightDetails.filter(
    (spot) => spot["Type"] === "Company information"
  ) : [];

  const roleOnlySpotlights = hasSpotlights ? role.spotlightDetails.filter(
    (spot) => spot["Type"] === "Role information"
  ) : [];

  const companyAndRoleSpotlights = hasSpotlights ? role.spotlightDetails.filter(
    (spot) => spot["Type"] === "Company and Role"
  ) : [];

  const hasOnlyCompanyAndRole = companyAndRoleSpotlights.length > 0 && 
    companyOnlySpotlights.length === 0 && 
    roleOnlySpotlights.length === 0;

  const companySpotlights = hasSpotlights && !hasOnlyCompanyAndRole
    ? role.spotlightDetails.filter(
        (spot) => spot["Type"] === "Company information" || spot["Type"] === "Company and Role"
      )
    : [];

  const roleSpotlights = hasSpotlights && !hasOnlyCompanyAndRole
    ? role.spotlightDetails.filter(
        (spot) => spot["Type"] === "Role information" || spot["Type"] === "Company and Role"
      )
    : [];

  const renderSpotlightVideo = (spot, index) => {
    const dropboxUrl = spot["URL Dropbox cleaned"] &&
      Array.isArray(spot["URL Dropbox cleaned"]) &&
      spot["URL Dropbox cleaned"].length > 0
        ? spot["URL Dropbox cleaned"][0]
        : spot["URL Dropbox cleaned"];

    const loomUrl = spot["URL Loom cleaned"] &&
      Array.isArray(spot["URL Loom cleaned"]) &&
      spot["URL Loom cleaned"].length > 0
        ? spot["URL Loom cleaned"][0]
        : spot["URL Loom cleaned"];

    if (dropboxUrl) {
      return (
        <video
          key={index}
          src={dropboxUrl}
          className="w-full h-56 border object-cover rounded-lg bg-gray-100 mb-3"
          controls
          ref={videoRef}
        />
      );
    } else if (loomUrl) {
      return (
        <div className="w-full h-56 rounded-lg mb-3 overflow-hidden relative">
          <iframe
            key={index}
            src={loomUrl}
            className="w-full h-full object-cover rounded-lg bg-gray-100"
            title={`Spotlight Video ${index}`}
          />
        </div>
      );
    }
    return null;
  };

  // Extract URLs from the user's rationale so we can render them as clickable links
  const extractUrlsFromText = (inputText) => {
    if (!inputText || typeof inputText !== 'string') return [];
    const urlPattern = /(https?:\/\/[\w.-]+(?:\/[\w\-._~:/?#[\]@!$&'()*+,;=%]*)?|www\.[\w.-]+(?:\/[\w\-._~:/?#[\]@!$&'()*+,;=%]*)?)/gi;
    const matches = inputText.match(urlPattern) || [];
    const normalizedMatches = matches.map((match) =>
      match.startsWith('www.') ? `https://${match}` : match
    );
    return Array.from(new Set(normalizedMatches));
  };

  const detectedUrls = extractUrlsFromText(rationale);

  // Build overlay content that visually highlights URLs inside the textarea area
  const renderOverlayNodes = (inputText) => {
    const splitter = /(https?:\/\/[\w.-]+(?:\/[\w\-._~:/?#[\]@!$&'()*+,;=%]*)?|www\.[\w.-]+(?:\/[\w\-._~:/?#[\]@!$&'()*+,;=%]*)?)/gi;
    const parts = (inputText || '').split(splitter);
    return parts.map((part, index) => {
      if (!part) return null;
      const isUrl = /^(https?:\/\/|www\.)/i.test(part);
      if (isUrl) {
        return (
          <span key={index} className="underline text-[#02B491]">
            {part}
          </span>
        );
      }
      return (
        <span key={index} className="text-transparent">
          {part}
        </span>
      );
    });
  };

  const handleTextareaScroll = (e) => {
    if (overlayContentRef.current) {
      const scrollTop = e?.target?.scrollTop || 0;
      overlayContentRef.current.style.transform = `translateY(-${scrollTop}px)`;
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="bg-white w-full max-w-5xl rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] relative"
      >
        {/* Close button - Top right of modal */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-30 text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Main Content - Split Layout */}
        <div className="flex flex-1 relative overflow-hidden">
          {/* Left Side - Role Details */}
          <AnimatePresence mode="wait">
            {!isCollapsed && (
              <motion.div
                initial={{ width: "42%", opacity: 1 }}
                animate={{ width: "42%", opacity: 1 }}
                exit={{ width: "0%", opacity: 0 }}
                transition={{ 
                  duration: 0.4, 
                  ease: [0.4, 0, 0.2, 1],
                  opacity: { duration: 0.2 }
                }}
                className="border-r border-gray-200 overflow-hidden flex-shrink-0 hidden md:flex flex-col"
              >
                <div className="h-full overflow-y-auto">
                  {/* Media Section */}
                  <div className="border-b border-gray-100">
                    {hasSpotlights ? (
                      <>
                        {/* Videos Section with Tabs */}
                        <div className="flex justify-between items-start px-4 pt-4">
                          <div className="flex">
                            {hasOnlyCompanyAndRole ? (
                              <button className="py-2 px-3 -mb-px border-b-2 border-[#02B491] text-[#02B491] font-medium text-sm">
                                Company and Role
                              </button>
                            ) : (
                              <>
                                <button
                                  className={`py-2 px-3 -mb-px text-sm transition-colors duration-200 ${
                                    activeTab === "company"
                                      ? "border-b-2 border-[#02B491] text-[#02B491] font-medium"
                                      : "text-gray-500 hover:text-gray-700"
                                  }`}
                                  onClick={() => setActiveTab("company")}
                                >
                                  Company
                                </button>
                                <button
                                  className={`py-2 px-3 -mb-px text-sm transition-colors duration-200 ${
                                    activeTab === "role"
                                      ? "border-b-2 border-[#02B491] text-[#02B491] font-medium"
                                      : "text-gray-500 hover:text-gray-700"
                                  }`}
                                  onClick={() => setActiveTab("role")}
                                >
                                  Role
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="p-4 pt-2">
                          {hasOnlyCompanyAndRole ? (
                            <div>
                              {companyAndRoleSpotlights.map((spot, index) => (
                                <div key={index}>
                                  {renderSpotlightVideo(spot, index)}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <>
                              {activeTab === "company" && companySpotlights.length > 0 ? (
                                <div>
                                  {companySpotlights.map((spot, index) => (
                                    <div key={index}>
                                      {renderSpotlightVideo(spot, index)}
                                    </div>
                                  ))}
                                </div>
                              ) : activeTab === "company" ? (
                                <p className="text-gray-500 text-xs py-2">
                                  No company spotlight available.
                                </p>
                              ) : null}
                              {activeTab === "role" && roleSpotlights.length > 0 ? (
                                <div>
                                  {roleSpotlights.map((spot, index) => (
                                    <div key={index}>
                                      {renderSpotlightVideo(spot, index)}
                                    </div>
                                  ))}
                                </div>
                              ) : activeTab === "role" ? (
                                <p className="text-gray-500 text-xs py-2">
                                  No role spotlight available.
                                </p>
                              ) : null}
                            </>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Image Section */}
                        <div className="p-4">
                          <img
                            src={getCoverImageUrl()}
                            alt={role["Role Title"] || "Role"}
                            className="w-full h-40 object-cover rounded-lg"
                          />
                        </div>
                      </>
                    )}
                  </div>

                  {/* Role Information */}
                  <div className="p-4">
                    <div className="mb-4">
                      <h3 className="font-semibold text-gray-900 mb-1 text-lg">
                        {role["Role Title"] || "(No role title)"}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {role["Name (from Startup)"] || "Unnamed Company"}
                      </p>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {role["Location (from Startup)"] && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                          <MapPin className="w-3 h-3 mr-1" />
                          {role["Location (from Startup)"]}
                        </span>
                      )}
                      {role["Compensation"] && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                          <Wallet className="w-3 h-3 mr-1" />
                          {role["Compensation"]}
                        </span>
                      )}
                      {role["Working style category"] && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200">
                          <Timer className="w-3 h-3 mr-1" />
                          {role["Working style category"]}
                        </span>
                      )}
                      {role["Team Size (from Startup)"] && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                          <Users className="w-3 h-3 mr-1" />
                          {role["Team Size (from Startup)"]} people
                        </span>
                      )}
                      {role["Funding Stage (from Startup)"] && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
                          <Building2 className="w-3 h-3 mr-1" />
                          {role["Funding Stage (from Startup)"]}
                        </span>
                      )}
                    </div>

                    {/* Company Description */}
                    {role["Startup Description (from Startup)"] && (
                      <div className="mt-6">
                        <h4 className="text-sm font-semibold text-gray-900 mb-3 border-b border-gray-200 pb-2">About the Company</h4>
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {role["Startup Description (from Startup)"]}
                        </p>
                      </div>
                    )}

                    {/* Key Skills */}
                    {role["Specific Experience"] && (
                      <div className="mt-6">
                        <h4 className="text-sm font-semibold text-gray-900 mb-3 border-b border-gray-200 pb-2">Key Skills</h4>
                        {/<[^>]+>/.test(role["Specific Experience"]) ? (
                          <div
                            className="text-sm text-gray-600 leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: role["Specific Experience"] }}
                          />
                        ) : (
                          <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
                            {role["Specific Experience"]}
                          </p>
                        )}
                      </div>
                    )}
                    {role["Job Description PDF"] && (
                      <div className="mt-6">
                        <h4 className="text-sm font-semibold text-gray-900 mb-3 border-b border-gray-200 pb-2">Job Description</h4>
                        <a
                          href={role["Job Description PDF"]}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 text-sm font-medium text-black bg-[#08f2c3] rounded-md hover:bg-[#06d1a7]"
                        >
                          View full job description
                          <ExternalLink className="w-4 h-4 ml-1" />
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Collapse Button - Positioned at top */}
          <motion.button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="absolute top-4 z-20 bg-white border border-gray-300 rounded-full shadow-md hover:shadow-lg hover:bg-gray-50 transition-all duration-200 hidden md:flex items-center group"
            style={{ 
              left: isCollapsed ? '16px' : 'calc(42% - 16px)',
            }}
            animate={{ 
              left: isCollapsed ? '16px' : 'calc(42% - 16px)',
            }}
            transition={{ 
              duration: 0.4, 
              ease: [0.4, 0, 0.2, 1] 
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title={isCollapsed ? "Show Role Info" : "Hide Role Info"}
          >
            <motion.div
              animate={{ rotate: isCollapsed ? 0 : 180 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="p-2"
            >
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </motion.div>
            {isCollapsed && (
              <motion.div
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: "auto" }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.2 }}
                className="px-2 py-2 text-xs text-gray-700 font-medium whitespace-nowrap border-l border-gray-200"
              >
                Role info
              </motion.div>
            )}
          </motion.button>

          {/* Right Side - Rationale Input */}
          <motion.div
            animate={{ 
              width: isCollapsed ? "100%" : "58%",
              marginLeft: isCollapsed ? "0%" : "0%"
            }}
            transition={{ 
              duration: 0.4, 
              ease: [0.4, 0, 0.2, 1]
            }}
            className="flex-1 flex flex-col min-w-0"
          >
            <div className="flex-1 overflow-y-auto">
              <div className={`p-4 transition-all duration-400 ${isCollapsed ? 'md:pl-4' : ''}`}>
                {/* Title for mobile */}
                <div className="mb-3 md:hidden">
                  <h2 className="text-lg font-semibold text-gray-900">Tell us why you'd be a good fit</h2>
                </div>

                {/* Title for desktop */}
                <div className="mb-4 hidden md:flex" style={{ marginTop: isCollapsed ? '40px' : '0px' }}>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Tell us why you'd be a good fit
                  </h2>
                </div>

                <p className="text-gray-600 text-sm mb-4">
                  Applying for: <strong className="text-gray-800">{roleTitle} at {Company}</strong>
                </p>

                <div className="text-sm text-gray-700 leading-relaxed mb-4 bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <p className="mb-3">
                    Whilst most roles need just two clicks to apply, <strong className="text-gray-800">{Company}</strong> requires a brief rationale (up to 100 words) to explain why you'd be a great fit.
                  </p>
                  <p className="font-medium text-gray-800 mb-2">Essential tips:</p>
                  <div className="space-y-1.5">
                    <div className="flex items-start space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700"><strong>Spell-check!</strong> Avoid mis-spelling the company name or making grammatical errors.</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700"><strong>Match their key requirements.</strong> Review the founder video and job description to understand what they want and show you're a good fit.</span>
                    </div>
                    {role["Rationale hard constraints"] ? (
                      <div className="flex items-start space-x-2 p-2.5 bg-red-50 border border-red-200 rounded-lg">
                        <div className="w-4 h-4 mt-0.5 flex-shrink-0 bg-red-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-bold">!</span>
                        </div>
                        <span className="text-red-800"><strong>Important Requirements:</strong> {role["Rationale hard constraints"]}</span>
                      </div>
                    ) : (
                      <div className="flex items-start space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700"><strong>Personalise to the startup</strong> - make it specific to them!</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="relative mb-4">
                  {/* Overlay that visually underlines URLs within the input area */}
                  <div className="pointer-events-none absolute inset-0 overflow-hidden z-0">
                    <div
                      ref={overlayContentRef}
                      className="whitespace-pre-wrap break-words px-4 py-3 text-sm leading-relaxed"
                    >
                      {renderOverlayNodes(rationale)}
                    </div>
                  </div>

                  <textarea
                    ref={textareaRef}
                    value={rationale}
                    onChange={(e) => setRationale(e.target.value)}
                    onScroll={handleTextareaScroll}
                    placeholder="Explain why you'd be a great fit for this role..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl resize-none focus:ring-2 focus:ring-[#02B491]/20 focus:border-[#02B491] transition-all text-sm leading-relaxed shadow-sm placeholder-gray-500 bg-transparent relative z-10"
                    rows={6}
                    maxLength={600}
                  />
                  <div className="absolute bottom-3 right-3 text-xs text-gray-500 bg-white/80 backdrop-blur-sm px-2 py-1 rounded-md z-20">
                    {rationale.split(' ').filter(word => word.length > 0).length}/100 words
                  </div>
                </div>

                {detectedUrls.length > 0 && (
                  <div className="mt-2 mb-4 text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <div className="font-medium mb-1">Links attached:</div>
                    <ul className="list-disc list-inside space-y-1">
                      {detectedUrls.map((url, index) => (
                        <li key={index}>
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#02B491] hover:underline inline-flex items-center gap-1 break-all"
                          >
                            {url}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Submit Button */}
                <div className="flex justify-center mt-6">
                  <button
                    onClick={handleSubmit}
                    disabled={!rationale.trim() || isSubmitting}
                    className="px-10 py-3 text-base font-semibold text-white bg-[#02B491] hover:bg-[#018268] rounded-lg transition-all duration-200 disabled:opacity-50 disabled:bg-gray-400 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 border-2 border-[#02B491] hover:border-[#018268] flex items-center gap-2"
                  >
                    {isSubmitting ? (
                      'Submitting...'
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Apply
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}

export default RationaleModal;